'use client'

import { StyleSheet, Text, View } from '@react-pdf/renderer'
import { Style } from '@react-pdf/types'
import parse, { HTMLReactParserOptions, domToReact } from 'html-react-parser'
import merge from 'lodash/merge'

const styles = StyleSheet.create({
  bulletPoint: {
    textAlign: 'left',
    width: 10,
  },
  em: { fontStyle: 'italic' },
  li: {
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
  },
  liContent: {
    flex: 1,
    textAlign: 'left',
    width: '100%',
  },
  p: { lineHeight: 1.1, marginBottom: 8 },
  strong: { fontWeight: 600 },
  u: { textDecoration: 'underline' },
  ul: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
})

interface IHtmlToPdfProps {
  htmlString: string
  stylesheet?: { [key: string]: Style }
}

export const HtmlToPdf = ({ htmlString, stylesheet }: IHtmlToPdfProps) => {
  const combinedStyles = merge({}, styles, stylesheet)

  // Track parent context
  const parentContext: string[] = []

  const options: HTMLReactParserOptions = {
    replace: ({ children, name, parent, type }) => {
      if (!name || type !== 'tag') return

      // Add current element to context when entering
      parentContext.push(name)

      // Check if we're inside a list item
      const isInsideLi = parentContext.includes('li')

      // Create result based on element type
      let result

      switch (name) {
        case 'p':
          // Special handling for paragraphs inside list items
          if (isInsideLi) {
            result = <Text>{domToReact(children, options)}</Text>
          } else {
            result = <Text style={combinedStyles.p}>{domToReact(children, options)}</Text>
          }
          break

        case 'strong':
          return <Text style={combinedStyles.strong}>{domToReact(children, options)}</Text>

        case 'em':
          return <Text style={combinedStyles.em}>{domToReact(children, options)}</Text>

        case 'u':
          return <Text style={combinedStyles.u}>{domToReact(children, options)}</Text>

        case 'ul':
          return <View style={combinedStyles.ul}>{domToReact(children, options)}</View>

        case 'li':
          return (
            <View style={combinedStyles.li}>
              <Text style={combinedStyles.bulletPoint}>•</Text>
              <Text style={combinedStyles.liContent}>{domToReact(children, options)}</Text>
            </View>
          )
      }

      // Remove current element from context when leaving
      parentContext.pop()

      return result
    },
  } as any //eslint-disable-line

  const parsed = parse(htmlString, options)
  return parsed
}
